import {FormControl, InputLabel, MenuItem, Select, FormHelperText} from "@mui/material";
import {useEffect, useState, useRef} from "react";
import {v4 as uuidV4} from 'uuid';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

const MySelect = ({label, options, value = '', editable = true, required = false, autoSelect, onChange, ...props}) => {

    const [labelId] = useState(() => uuidV4());
    const [invalidValue, setInvalidValue] = useState(null);

    // Initialize with the provided value only when options are already available;
    // otherwise start blank so MUI never sees a value with no MenuItem to match.
    const [selectedValue, setSelectedValue] = useState(() => (options?.length > 0 ? value : ''));

    // Helper to determine if a value represents a blank/clear selection
    const isBlankValue = (v) => v === '' || v === 0 || v === null || typeof v === 'undefined';

    // Handle auto-select logic and validation
    useEffect(() => {
        // Reset invalid value
        setInvalidValue(null);

        // Options not loaded yet: render blank until they arrive, otherwise MUI
        // warns about an out-of-range value with no MenuItems to match.
        if (!options || options.length === 0) {
            setSelectedValue('');
            return;
        }

        // If there's only one option, only auto-select it when autoSelect is true
        // and avoid auto-selecting the blank/clear option in update/read scenarios.
        if (options.length === 1) {
            const onlyOptionId = options[0].id;
            if (autoSelect && !isBlankValue(onlyOptionId)) {
                setSelectedValue(onlyOptionId);
                if (value !== onlyOptionId) {
                    onChange(onlyOptionId);
                }
            } else {
                // keep as blank/unchanged
                setSelectedValue(isBlankValue(value) ? '' : value);
            }
            return;
        }

        // Check if value exists in options
        const valueExists = !isBlankValue(value) && options.some(option => option.id === value);

        if (valueExists) {
            // Value is valid, use it
            setSelectedValue(value);
        } else if (!isBlankValue(value)) {
            // Value doesn't match any option, show alert
            setInvalidValue(value);

            // Auto-select first option if autoSelect is true
            if (autoSelect) {
                const firstOptionId = options[0].id;
                setSelectedValue(firstOptionId);
                onChange(firstOptionId);
            } else {
                setSelectedValue('');
            }
        } else if (autoSelect && options.length > 0) {
            // Value is empty and autoSelect is true, select first option
            const firstOptionId = options[0].id;
            setSelectedValue(firstOptionId);
            onChange(firstOptionId);
        } else {
            // Value is empty and autoSelect is false
            setSelectedValue('');
        }
    }, [options, value, autoSelect]);

    // Ensure the dropdown shows "-- CLEAR --" for blank options when open,
    // but render nothing in the closed state when blank is selected.
    const getOptionDisplayName = (option) => {
        if (!option) return '';
        const name = option.name;
        if (name === '' || isBlankValue(option.id)) return '-- CLEAR --';
        return name;
    };

    return (
        <FormControl
            fullWidth
            variant="outlined"
            margin="dense"
            size="small"
            required={required}
        >
            <InputLabel
                id={labelId}
            >
                {label}
            </InputLabel>
            <Select
                labelId={labelId}
                label={label}
                inputProps={{readOnly: !editable}}
                onChange={(event) => {
                    setSelectedValue(event.target.value);
                    onChange(event.target.value);
                }}
                value={selectedValue}
                disabled={!editable}
                IconComponent={KeyboardArrowDownOutlinedIcon}
                renderValue={(selected) => {
                    // Show nothing when blank/clear is selected
                    if (isBlankValue(selected)) return '';
                    const selectedOption = options?.find(o => o.id === selected);
                    // If the selected option's name is blank or "-- CLEAR --", still render empty
                    const name = selectedOption?.name;
                    if (!name || name === '-- CLEAR --') return '';
                    return String(name);
                }}
                sx={{
                    backgroundColor: 'white',
                }}
                {...props}
            >
                {options?.map((option) => {
                    return (
                        <MenuItem
                            key={option.id}
                            value={option.id}
                        >
                            {getOptionDisplayName(option)}
                        </MenuItem>
                    );
                })}
            </Select>
            {invalidValue && (
                <FormHelperText error>
                    Failed to auto-select {invalidValue}
                </FormHelperText>
            )}
        </FormControl>
    );
}

export default MySelect;
